import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './pStyles/MapView.css';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface Task {
  id: number;
  title: string;
  description: string;
  location: string;
  project_name?: string;
  project_id?: number;
  status: string;
  due_date?: string;
}

interface Project {
  id: number;
  name: string;
  description: string;
  status: string;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app';

// South African location coordinates
const LOCATION_COORDINATES: { [key: string]: [number, number] } = {
  'Cape Town CBD': [-33.9249, 18.4241],
  'Bellville': [-33.8969, 18.6286],
  'Rondebosch': [-33.9633, 18.4725],
  'Claremont': [-33.9806, 18.4653],
  'Durbanville': [-33.8303, 18.6419],
  'Paarl': [-33.7348, 18.9612],
  'Stellenbosch': [-33.9321, 18.8602],
  'Milnerton': [-33.8708, 18.5022],
  'Johannesburg': [-26.2041, 28.0473],
  'Pretoria': [-25.7479, 28.2293],
  'Durban': [-29.8587, 31.0218],
  'Port Elizabeth': [-33.9608, 25.6022],
  'Bloemfontein': [-29.0852, 26.1596],
  'East London': [-33.0292, 27.8546],
  'Kimberley': [-28.7282, 24.7499],
  'Polokwane': [-23.9008, 29.4512],
  'Nelspruit': [-25.4745, 30.9703],
  'Rustenburg': [-25.6500, 27.2400],
  'Welkom': [-27.9774, 26.7351]
};

export default function MapView() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mapReady, setMapReady] = useState(false);

  // Fetch tasks from projects
  useEffect(() => {
    const fetchTasks = async () => {
      try {
        setLoading(true);
        console.log('Fetching tasks from API:', `${API_BASE_URL}/api/projects`);
        
        // First, fetch all projects
        const projectsResponse = await fetch(`${API_BASE_URL}/api/projects`);
        if (!projectsResponse.ok) {
          throw new Error('Failed to fetch projects');
        }
        
        const projects: Project[] = await projectsResponse.json();
        console.log('Fetched projects:', projects);
        const allTasks: Task[] = [];
        
        // Fetch tasks for each project
        const projectPromises = projects.map(async (project: Project) => {
          try {
            const tasksResponse = await fetch(`${API_BASE_URL}/api/projects/${project.id}/tasks`);
            if (tasksResponse.ok) {
              const tasks: Task[] = await tasksResponse.json();
              return tasks.map(task => ({
                ...task,
                project_name: project.name,
                project_id: project.id
              }));
            }
            return [];
          } catch (error) {
            console.error(`Error fetching tasks for project ${project.id}:`, error);
            return [];
          }
        });

        const projectTasksArrays = await Promise.all(projectPromises);
        const allTasksFlat = projectTasksArrays.flat();
        
        // Filter tasks that have locations
        const tasksWithLocations = allTasksFlat.filter(task => 
          task.location && task.location.trim() !== ''
        );

        console.log('Tasks with locations:', tasksWithLocations);
        setTasks(tasksWithLocations);
      } catch (error) {
        console.error('Error fetching tasks:', error);
        setError('Failed to fetch tasks');
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, []);

  // Group tasks by location for markers
  const tasksByLocation = tasks.reduce((acc, task) => {
    if (!acc[task.location]) {
      acc[task.location] = [];
    }
    acc[task.location].push(task);
    return acc;
  }, {} as { [key: string]: Task[] });

  if (loading) {
    return (
      <div className="map-view">
        <div className="map-header">
          <h1>Task Locations Map</h1>
          <p>Explore volunteer opportunities across South Africa</p>
        </div>
        <div className="map-loading">
          <div className="loading-spinner"></div>
          <p>Loading map...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="map-view">
        <div className="map-header">
          <h1>Task Locations Map</h1>
          <p>Explore volunteer opportunities across South Africa</p>
        </div>
        <div className="map-error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="map-view">
      <div className="map-header">
        <h1>Task Locations Map</h1>
        <p>Explore volunteer opportunities in the Western Cape</p>
      </div>

      <div className="map-content">
        <div className="map-wrapper">
          <MapContainer
            center={[-33.9, 18.4]} // Center of Western Cape (Cape Town area)
            zoom={8}
            style={{ height: '500px', width: '100%' }}
            whenReady={() => setMapReady(true)}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            {/* Render markers for each location with tasks */}
            {Object.entries(tasksByLocation).map(([location, locationTasks]) => {
              const coords = LOCATION_COORDINATES[location];
              if (!coords) {
                console.log(`No coordinates found for location: ${location}`);
                return null;
              }

              const hasActiveTasks = locationTasks.some(task => task.status === 'Active');
              const markerColor = hasActiveTasks ? 'green' : 'orange';

              return (
                <Marker
                  key={location}
                  position={coords}
                  icon={L.divIcon({
                    className: 'custom-marker',
                    html: `<div style="
                      background-color: ${markerColor};
                      width: 20px;
                      height: 20px;
                      border-radius: 50%;
                      border: 3px solid white;
                      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                      cursor: pointer;
                      transition: transform 0.2s ease;
                    " 
                    title="${location}: ${locationTasks.map(task => task.title).join(', ')}"
                    onmouseover="this.style.transform='scale(1.2)'"
                    onmouseout="this.style.transform='scale(1)'"
                    ></div>`,
                    iconSize: [20, 20],
                    iconAnchor: [10, 10]
                  })}
                >
                  <Popup>
                    <div style={{ minWidth: '200px' }}>
                      <h3 style={{ margin: '0 0 8px 0', color: '#1f2937', fontSize: '16px' }}>
                        📍 {location}
                      </h3>
                      <p style={{ margin: '0 0 10px 0', color: '#6b7280', fontSize: '14px' }}>
                        {locationTasks.length} task{locationTasks.length !== 1 ? 's' : ''}
                      </p>
                      <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '8px' }}>
                        {locationTasks.map((task) => (
                          <div key={task.id} style={{ 
                            marginBottom: '8px', 
                            padding: '6px',
                            backgroundColor: '#f9fafb',
                            borderRadius: '4px',
                            border: '1px solid #e5e7eb'
                          }}>
                            <div style={{ fontWeight: '600', color: '#1f2937', fontSize: '14px' }}>
                              {task.title}
                            </div>
                            <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '2px' }}>
                              📋 {task.project_name}
                            </div>
                            <div style={{ color: '#6b7280', fontSize: '12px' }}>
                              Status: <span style={{ 
                                color: task.status === 'Active' ? '#10b981' : '#f59e0b',
                                fontWeight: '500'
                              }}>
                                {task.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        </div>

        <div className="task-locations">
          <h3>Task Locations</h3>
          <div className="locations-list">
            {Object.entries(tasksByLocation).map(([location, locationTasks]) => (
              <div key={location} className="location-item">
                <h4>{location}</h4>
                <p>{locationTasks.length} task(s)</p>
                <div className="tasks-list">
                  {locationTasks.slice(0, 2).map((task) => (
                    <div key={task.id} className="task-item">
                      <span className="task-title">{task.title}</span>
                      <span className="task-category">- {task.project_name}</span>
                    </div>
                  ))}
                  {locationTasks.length > 2 && (
                    <p className="more-tasks">+ {locationTasks.length - 2} more tasks</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}